import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { useNavigate } from "react-router-dom";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import VisaTabs from "../AllTabs/VisaTabs";
import moment from "moment";
import DatePicker from "react-datepicker"; // Import the new date picker
import "react-datepicker/dist/react-datepicker.css"; // Import the styles
import { RiCalendarLine } from "react-icons/ri";
import "./VisaRequests.css";

const VisaSupplierReports = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const isInitialMount = useRef(true);

  // Filters state
  const [filters, setFilters] = useState({
    fromDate: null,
    toDate: null,
    supplierId: "",
    visaRequestStatus: "",
  });

  // Get current month date range
  const getCurrentMonthDateRange = () => {
    const startOfMonth = moment().startOf("month");
    const endOfMonth = moment().endOf("month");

    return {
      fromDate: startOfMonth.toDate(),
      toDate: endOfMonth.toDate(),
    };
  };

  // Fetch suppliers for filter dropdown
  // const GetSuppliers = async () => {
  //   try {
  //     const res = await apiService.getAll(`${config.GETALLSUPPLIERS}`);
  //     if (res && res.length > 0) {
  //       setSuppliers(res);
  //     }
  //   } catch (error) {
  //     console.error("Error fetching suppliers:", error);
  //   }
  // };

  // Fetch report data
  const GetData = async (currentFilters) => {
    if (!user?.cid) return;

    setIsLoading(true);
    try {
      let url = `${config.GETVISASUPPLIERREPORT}/${user.cid}`;

      const params = new URLSearchParams();

      // Add filters to params
      Object.keys(currentFilters).forEach((key) => {
        const value = currentFilters[key];
        if (value && value !== "" && value !== "all") {
          if (key === "fromDate" || key === "toDate") {
            // Format date for API
            params.append(key, new Date(value).toISOString());
          } else {
            params.append(key, value);
          }
        }
      });

      // Add date filters with proper formatting
      if (currentFilters.fromDate) {
        params.append(
          "fromDate",
          new Date(currentFilters.fromDate).toISOString()
        );
      }
      if (currentFilters.toDate) {
        params.append("toDate", new Date(currentFilters.toDate).toISOString());
      }

      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      const res = await apiService.getAll(url);
      setReports(res || []);
    } catch (error) {
      console.error("Error fetching visa supplier report:", error);
      setReports([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle date changes
  const handleDateChange = (date, field) => {
    setFilters((prev) => ({
      ...prev,
      [field]: date,
    }));
  };

  // Reset filters
  const resetFilters = () => {
    const defaultDates = getCurrentMonthDateRange();
    setFilters({
      fromDate: defaultDates.fromDate,
      toDate: defaultDates.toDate,
      supplierId: "",
      visaRequestStatus: "",
    });
  };

  // Calculate totals
  const calculateTotals = () => {
    const totals = {
      totalSupplierPrice: 0,
      totalApplicants: 0,
    };

    reports.forEach((item) => {
      totals.totalSupplierPrice += parseFloat(item.supplierPrice) || 0;
      totals.totalApplicants += parseInt(item.noOfApplicants) || 0;
    });

    return totals;
  };

  // Status badge classes
  const getStatusBadgeClass = (status) => {
    if (!status) return "badge bg-secondary";

    switch (status.toUpperCase()) {
      case "APPROVED":
        return "badge bg-success";
      case "PENDING":
      case "PROCESSING":
      case "DOCUMENT_PENDING":
        return "badge bg-warning text-dark";
      case "REJECTED":
        return "badge bg-danger";
      case "SUBMITTED":
      case "NEW":
        return "badge bg-info";
      case "DOCUMENTS_MISSING":
        return "badge bg-danger";
      default:
        return "badge bg-secondary";
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return "0.00";
    return parseFloat(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Custom input component for date picker
  const CustomDateInput = React.forwardRef(
    ({ value, onClick, placeholder }, ref) => (
      <div className="input-group">
        <input
          type="text"
          className="form-control form-control-md"
          value={value}
          onClick={onClick}
          ref={ref}
          readOnly
          placeholder={placeholder}
        />
        <button className="btn btn-secondary" type="button" onClick={onClick}>
          <RiCalendarLine />
        </button>
      </div>
    )
  );

  useEffect(() => {
    if (!loading && !user) {
      navigate("/");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      const defaultDates = getCurrentMonthDateRange();
      setFilters((prev) => ({
        ...prev,
        fromDate: defaultDates.fromDate,
        toDate: defaultDates.toDate,
      }));
      // GetSuppliers();
      return;
    }

    const debounceTimer = setTimeout(() => {
      GetData(filters);
    }, 350);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [user, filters]);

  const totals = calculateTotals();

  if (loading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
  }

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="card">
              <div className="p-2">
                <VisaTabs pageName="VISA SUPPLIER REPORTS" />
              </div>
              <div className="card-body">
                {/* Filters Section - Updated with new calendar */}
                <div className="row g-2 mb-3">
                  <div className="col-xl-2 col-md-4">
                    <DatePicker
                      selected={filters.fromDate}
                      onChange={(date) => handleDateChange(date, "fromDate")}
                      customInput={<CustomDateInput placeholder="From Date" />}
                      dateFormat="dd-MMM-yyyy"
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      yearDropdownItemNumber={50}
                      scrollableYearDropdown
                      className="form-control form-control-md"
                      selectsStart
                      startDate={filters.fromDate}
                      endDate={filters.toDate}
                    />
                  </div>

                  <div className="col-xl-2 col-md-4">
                    <DatePicker
                      selected={filters.toDate}
                      onChange={(date) => handleDateChange(date, "toDate")}
                      customInput={<CustomDateInput placeholder="To Date" />}
                      dateFormat="dd-MMM-yyyy"
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      yearDropdownItemNumber={50}
                      scrollableYearDropdown
                      className="form-control form-control-md"
                      selectsEnd
                      startDate={filters.fromDate}
                      endDate={filters.toDate}
                      minDate={filters.fromDate}
                    />
                  </div>

                  {/* <div className="col-xl-3 col-md-4">
                    <select
                      className="form-select form-select-md"
                      name="supplierId"
                      value={filters.supplierId}
                      onChange={handleFilterChange}
                    >
                      <option value="all">All Suppliers</option>
                      {suppliers.map((supplier) => (
                        <option
                          key={supplier.supplierId}
                          value={supplier.supplierId}
                        >
                          {supplier.supplierName}
                        </option>
                      ))}
                    </select>
                  </div> */}

                  <div className="col-xl-3 col-md-4">
                    <select
                      className="form-select form-select-md"
                      name="visaRequestStatus"
                      value={filters.visaRequestStatus}
                      onChange={handleFilterChange}
                    >
                      <option value="all">All Visa Statuses</option>
                      <option value="New">New</option>
                      <option value="PENDING">Pending</option>
                      <option value="PROCESSING">Processing</option>
                      <option value="SUBMITTED">Submitted</option>
                      <option value="APPROVED">Approved</option>
                      <option value="REJECTED">Rejected</option>
                      <option value="DOCUMENTS_MISSING">
                        Documents Missing
                      </option>
                      <option value="DOCUMENT_PENDING">Document Pending</option>
                    </select>
                  </div>

                  <div className="col-xl-2 col-md-4 d-flex align-items-start">
                    <button
                      className="btn btn-md btn-danger"
                      onClick={resetFilters}
                    >
                      <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                      Reset
                    </button>
                  </div>
                </div>

                {isLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading supplier reports...</p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-centered table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Reference No</th>
                            <th>Visa</th>
                            <th>Supplier</th>
                            <th>No. of Applicants</th>
                            <th>Supplier Price</th>
                            <th>Visa Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reports && reports.length > 0 ? (
                            reports.map((report, index) => (
                              <tr key={index}>
                                <td className="fw-semibold">
                                  {report.visaReferenceNo}
                                </td>
                                <td>
                                  <div
                                    className="fw-semibold text-wrap"
                                    style={{ maxWidth: "200px" }}
                                  >
                                    {report.visaName || "-"}
                                    {report.countryName && (
                                      <small className="text-muted d-block">
                                        {report.countryName}
                                      </small>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div>
                                    {report.supplierName || "Not assigned"}
                                  </div>
                                </td>
                                <td>
                                  <div className="text-danger fw-semibold">
                                    {report.noOfApplicants || 0}
                                  </div>
                                </td>
                                <td>
                                  {report.supplierPrice ? (
                                    <span className="fw-bold text-primary">
                                      INR{formatCurrency(report.supplierPrice)}
                                    </span>
                                  ) : (
                                    <span className="fw-bold text-primary">
                                      -
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <span
                                    className={getStatusBadgeClass(
                                      report.visaRequestStatus
                                    )}
                                  >
                                    {report.visaRequestStatus}
                                  </span>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6" className="text-center py-4">
                                No supplier reports found
                              </td>
                            </tr>
                          )}
                        </tbody>
                        {/* Grand Total Row */}
                        {reports.length > 0 && (
                          <tfoot className="table-active">
                            <tr className="fw-bold">
                              <td colSpan="3" className="text-end">
                                Grand Total:
                              </td>
                              <td className="text-danger">
                                {totals.totalApplicants}
                              </td>
                              <td className="text-primary">
                                INR{formatCurrency(totals.totalSupplierPrice)}
                              </td>
                              <td></td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisaSupplierReports;
